// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React from 'react'
import { Anchor, Button, Col, Input, message, Modal, Radio, Row, Select, Switch, Typography } from 'antd'
import { connect } from 'react-redux'
import { ModalProps } from 'antd/lib/modal'
import { RootState } from 'store'
import {
  DeviceType,
  EnvironmentsOptions,
  IDevice,
  LiteGroupDeviceDirectionNames,
  QuotaCycle,
  TaskType
} from 'constants/device'
import {
  getDeviceSetting,
  ICameraGroupQuerySetting,
  IGroupCamera,
  updateCameraGroupSetting,
  updateDeviceSetting
} from 'api/setting'
import { fetchDevicesInGroup, getDeviceById, IDeviceUdpateParam, updateDevice } from 'api/devices'
import {
  CameraDirection,
  cameraDirectionDictionary,
  DistanceBasedIntervalrange,
  GPSUploadIntervalRange,
  ImageryFormat,
  ImageryResolution,
  IMUUploadIntervalRange,
  IntervalType,
  MonthlyDataQuotaRange,
  TimeBasedIntervalRange,
  UploadFormat,
  UploadMethod,
  VideoResolution
} from 'constants/setting'
import { IDeviceState } from 'models/device-update-model'
import { ISettingValues } from 'models/setting-model'
import TimePicker from 'components/time-picker'
import UserSearch from 'components/user-search'
import { BarChartOutlined, CameraOutlined, CloudUploadOutlined, ForkOutlined, UserOutlined } from '@ant-design/icons'
import { SettingCard } from 'components/setting-card'
import { ComposedTitle } from 'components/composed-title'
import { AlertThresholdOptions, CycleStartDateOptions, HardThresholdOptions } from 'components/general-setting'
import MaskLoading from 'components/MaskLoading'
import { ComposedDeviceSettingModel } from 'models/composed-device-setting-model'
import { SupportedAIModels } from '../../constants/AIModels'
import { ImageCompressionRatioOptions, ImageCompressionRatioOptionsPrd } from 'components/internal-setting'
import CameraGroupSetting, { ISlaveCamera, settingMode, values } from 'components/general-setting/CameraGroupSetting'
import isDev from 'utils/env'
import { formatNumberInput } from 'utils/format'
import { keyBy } from 'lodash'

function mapState(rootState: RootState) {
  return {
    cities: rootState.city.cityList
  }
}

export type IDetailEditingModalProps = ModalProps &
  ReturnType<typeof mapState> & {
  device: { deviceID: string, groupID: number, cityID: number }
}


interface ICameraGroupSetting {
  left?: IGroupCamera
  right?: IGroupCamera
  front?: IGroupCamera
  back?: IGroupCamera
}



export interface IDetailEditingModalState {
  loading: boolean
  detail: {
    device: IDeviceState
    setting: ISettingValues
  }
  groupCameraSetting: ICameraGroupSetting
  groupSettingDirty: boolean
  saving: boolean
  notFound: boolean
  settingDirty: boolean
  deviceDirty: boolean
  testMode: boolean
  quotaCycleType: number
  oldCycleStart: number
}

class DetailEditingModal extends React.Component<IDetailEditingModalProps, IDetailEditingModalState> {
  private editingModel: ComposedDeviceSettingModel
  private anchorContainer = React.createRef<HTMLDivElement>()
  private imageCompressionOptions: JSX.Element[]

  state: IDetailEditingModalState = {
    loading: false,
    detail: {
      device: null,
      setting: null
    },
    groupCameraSetting: null,
    groupSettingDirty: false,
    saving: false,
    notFound: false,
    settingDirty: false,
    deviceDirty: false,
    testMode: false,
    quotaCycleType: null,
    oldCycleStart: null
  }
  private originGroupCamera: values = null

  constructor(props) {
    super(props)
    this.imageCompressionOptions = isDev() ? ImageCompressionRatioOptions : ImageCompressionRatioOptionsPrd
  }

  componentDidMount() {
    const { device: { deviceID, groupID } } = this.props
    this.loadDetail(deviceID, groupID)
  }

  componentDidUpdate(prevProps: IDetailEditingModalProps) {
    if (prevProps.device.deviceID !== this.props.device.deviceID) {
      this.loadDetail(this.props.device.deviceID)
    }
  }

  componentWillUnmount() {
    if (this.editingModel) {
      this.editingModel.dispose()
    }
  }

  private loadDetail = async (deviceID: string, groupID?: number) => {
    this.setState({
      loading: true
    })
    let deviceRes: IDevice = null
    let settingRes = null
    let notFound = false
    try {
      deviceRes = await getDeviceById(deviceID)
    } catch (error) {
      notFound = true
    }

    if (notFound) {
      this.setState({
        notFound,
        loading: false
      })
      return
    }

    if (deviceRes.testMode) {
      try {
        settingRes = await getDeviceSetting(deviceID)
      } catch (error) {
      }
    }

    if (this.editingModel) {
      this.editingModel.dispose()
    }
    if (groupID !== null && groupID !== 0) {
      const { devices: groupDevices } = await fetchDevicesInGroup(groupID)
      this.originGroupCamera = keyBy(groupDevices, (o) => LiteGroupDeviceDirectionNames[o.direction].toLowerCase())
    }


    let deviceState = null
    let settingState = null

    if (settingRes && deviceRes.testMode) {
      this.editingModel = new ComposedDeviceSettingModel(deviceRes, settingRes)
    } else {
      this.editingModel = new ComposedDeviceSettingModel(deviceRes, null)
    }
    this.editingModel.emitter.on('change', state => {
      this.setState({
        detail: {
          setting: state.setting,
          device: state.device
        },
        settingDirty: state.settingDirty,
        deviceDirty: state.deviceDirty
      })
    })
    settingState = this.editingModel.getState().setting
    deviceState = this.editingModel.getState().device
    this.setState({
      detail: {
        device: deviceState,
        setting: settingState
      },
      loading: false,
      testMode: deviceRes.testMode,
      oldCycleStart: deviceRes.billingCycleStart,
      quotaCycleType:
        deviceRes.billingCycleStart === QuotaCycle.CalendarMonth ? QuotaCycle.CalendarMonth : QuotaCycle.PickedDay
    })
  }

  private renderAnchor = () => {
    const { testMode } = this.state
    const { device } = this.state.detail

    if (testMode && device.testMode) {
      return (
        <Anchor
          offsetTop={24}
          className='horizontal-anchor'
          affix={false}
          getContainer={() => this.anchorContainer.current || window}
        >
          <Anchor.Link
            href='#modal-camera'
            title={
              <span>
                <CameraOutlined style={{ marginRight: 12 }} />
                Camera
              </span>
            }
          />
          <Anchor.Link
            href='#modal-imageryUpload'
            title={
              <span>
                <CloudUploadOutlined style={{ marginRight: 12 }} />
                Imagery Upload
              </span>
            }
          />
          <Anchor.Link
            href='#modal-user'
            title={
              <span>
                <UserOutlined style={{ marginRight: 12 }} />
                User
              </span>
            }
          />
          <Anchor.Link
            href='#modal-aiModels'
            title={
              <span>
                <ForkOutlined style={{ marginRight: 12 }} />
                AI Models
              </span>
            }
          />
          <Anchor.Link
            href='#modal-dataUsage'
            title={
              <span>
                <BarChartOutlined style={{ marginRight: 12 }} />
                Data Quota
              </span>
            }
          />
        </Anchor>
      )
    } else {
      return (
        <Anchor
          offsetTop={24}
          className='horizontal-anchor'
          affix={false}
          getContainer={() => this.anchorContainer.current || window}
        >
          <Anchor.Link
            href='#modal-camera'
            title={
              <span>
                <CameraOutlined style={{ marginRight: 12 }} />
                Camera
              </span>
            }
          />
          <Anchor.Link
            href='#modal-user'
            title={
              <span>
                <UserOutlined style={{ marginRight: 12 }} />
                User
              </span>
            }
          />
          <Anchor.Link
            href='#modal-dataUsage'
            title={
              <span>
                <BarChartOutlined style={{ marginRight: 12 }} />
                Data Quota
              </span>
            }
          />
        </Anchor>
      )
    }
  }

  private handleSettingChange = (key, value) => {
    switch (key) {
      case 'imageryFmt':
        if (value === true) {
          value = ImageryFormat.H256
        } else {
          value = ImageryFormat.JPG
        }
        break
      case 'trafficQuota':
        value = value !== '' ? parseInt(value) : MonthlyDataQuotaRange[0]
        if (value > MonthlyDataQuotaRange[1]) {
          value = MonthlyDataQuotaRange[1]
        }
        if (value < MonthlyDataQuotaRange[0]) {
          value = MonthlyDataQuotaRange[0]
        }
        break
      case 'gpsUploadInterval':
        value = value !== '' ? parseInt(value) : GPSUploadIntervalRange[0]
        if (value > GPSUploadIntervalRange[1]) {
          value = GPSUploadIntervalRange[1]
        }
        if (value < GPSUploadIntervalRange[0]) {
          value = GPSUploadIntervalRange[0]
        }
        break
      case 'captureInterval':
        value = value !== '' ? parseInt(value) : TimeBasedIntervalRange[0]
        if (value > TimeBasedIntervalRange[1]) {
          value = TimeBasedIntervalRange[1]
        }
        if (value < TimeBasedIntervalRange[0]) {
          value = TimeBasedIntervalRange[0]
        }
        break
      case 'captureDistanceInterval':
        value = value !== '' ? parseInt(value) : DistanceBasedIntervalrange[0]
        if (value > DistanceBasedIntervalrange[1]) {
          value = DistanceBasedIntervalrange[1]
        }
        if (value < DistanceBasedIntervalrange[0]) {
          value = DistanceBasedIntervalrange[0]
        }
        break
      case 'imuUploadInterval':
        value = value !== '' ? parseInt(value) : IMUUploadIntervalRange[0]
        if (value > IMUUploadIntervalRange[1]) {
          value = IMUUploadIntervalRange[1]
        }
        if (value < IMUUploadIntervalRange[0]) {
          value = IMUUploadIntervalRange[0]
        }
        break
      case 'cityID':
        this.editingModel.setDevice(key, value)
        break
      default:
        break
    }
    this.editingModel.setSetting(key, value)
  }

  private handleDeviceChange = (key, value) => {
    const { device } = this.state.detail
    if (key === 'deviceType' && device.deviceType === DeviceType.Lite360Master) {
      const title = (
        <div style={{ fontFamily: 'SanomatGrabApp' }}>
          <header style={{ fontSize: '16px', marginBottom: '15px' }}>
            You are detaching all cameras from this 360-lite group.
          </header>
          <p style={{ fontSize: '16px', color: 'rgba(0,0,0,0.65)' }}>
            Any 360 images that has not been uploaded will no longer be combined into 360 images. Are you sure you want
            to continue?
          </p>
        </div>
      )
      Modal.confirm({
        title: title,
        centered: true,
        width: 490,
        onOk: () => {
          this.editingModel.setDevice(key, value)
        },
        cancelText: 'Return to settings',
        okText: 'Continue',
        okButtonProps: { type: 'primary', danger: true }
      })
    } else {
      this.editingModel.setDevice(key, value)
    }
  }

  private handleQuotaCycleType = value => {
    const { oldCycleStart } = this.state
    if (value === QuotaCycle.CalendarMonth) {
      this.editingModel.setDevice('billingCycleStart', value)
    } else if (value === QuotaCycle.PickedDay) {
      const billingCycleStart = oldCycleStart === QuotaCycle.CalendarMonth ? QuotaCycle.PickedDay : oldCycleStart
      this.editingModel.setDevice('billingCycleStart', billingCycleStart)
    }
    this.setState({ quotaCycleType: value })
  }

  private handleTimeChange = values => {
    if (values[0].isSame(values[1])) {
      return
    }
    this.editingModel.setSetting('workTimeStart', values[0])
    this.editingModel.setSetting('workTimeEnd', values[1])
  }

  handleCameraGroupSettingChange = (value: ISlaveCamera) => {
    const { direction, deviceID = '' } = value
    const groupCameraSetting = this.state.groupCameraSetting || this.originGroupCamera
    const hasChanged = this.checkGroupSettingDirty(value)
    this.setState({
      groupCameraSetting: {
        ...groupCameraSetting,
        [direction]: { direction: cameraDirectionDictionary[direction], deviceID }
      },
      groupSettingDirty: hasChanged
    })
  }
  checkGroupSettingDirty = (value: ISlaveCamera): boolean => {
    const { deviceID, direction } = value
    let hasChanged: boolean
    if (deviceID) {
      hasChanged = !(this.originGroupCamera && (this.originGroupCamera as object)[direction]?.deviceID === deviceID)
      return hasChanged
    } else {
      hasChanged = this.originGroupCamera && (this.originGroupCamera as object)[direction]?.deviceID
      return hasChanged
    }
  }

  renderIfTestModeEnabled = () => {
    const { device, setting } = this.state.detail
    const { quotaCycleType } = this.state
    const { cities, device: { deviceID, cityID } } = this.props
    let mode = device.deviceType === DeviceType.Lite360Master ?  settingMode.operate : settingMode.query
    let originGroupCamera = { front: { deviceID, direction: CameraDirection.front, cityID } }
    if (this.originGroupCamera !== null) {
      Object.keys(this.originGroupCamera).forEach(key => {
        originGroupCamera[key] = { ...this.originGroupCamera[key], cityID }
      })
    }
    return (
      <>
        <SettingCard id='modal-camera' title='Camera' icon={<CameraOutlined />} className='detail-item'>
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>Test Mode</Col>
              <Col>
                <Switch checked={device.testMode} onChange={v => this.handleDeviceChange('testMode', v)} />
              </Col>
            </Row>
          </SettingCard.Row>
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>Camera Type</Col>
              <Col>
                <Select
                  value={device.deviceType === DeviceType.Lite360Slave ? DeviceType.Lite360Master : device.deviceType}
                  onChange={v => this.handleDeviceChange('deviceType', v)}
                  style={{ width: 200 }}
                >
                  <Select.Option value={DeviceType.TwoW}>2W</Select.Option>
                  <Select.Option value={DeviceType.FourW}>4W</Select.Option>
                  <Select.Option value={DeviceType.Lite360Master}
                                 disabled={Number(deviceID.slice(-1)) < 3}>360-Lite</Select.Option>
                </Select>
              </Col>
            </Row>
          </SettingCard.Row>
          {
            device.deviceType === DeviceType.Lite360Master && (
              <SettingCard.Row className={device.deviceType === DeviceType.Lite360Master ? '' : 'hide'}>
                <CameraGroupSetting
                                    values={{ ...originGroupCamera }}
                                    onChange={this.handleCameraGroupSettingChange}
                                    mode={mode} />
              </SettingCard.Row>
            )
          }
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>Task Type</Col>
              <Col>
                <Select
                  value={device.taskType}
                  onChange={v => this.handleDeviceChange('taskType', v)}
                  style={{ width: 200 }}
                >
                  <Select.Option value={TaskType.Passive}>Passive</Select.Option>
                  <Select.Option value={TaskType.Active}>Active</Select.Option>
                </Select>
              </Col>
            </Row>
          </SettingCard.Row>
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>Current Location</Col>
              <Col>
                <Select
                  value={device.cityID}
                  onChange={v => this.handleSettingChange('cityID', v)}
                  style={{ width: 200 }}
                >
                  {cities.map(d => (
                    <Select.Option value={d.id} key={d.id}>
                      {d.name}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </SettingCard.Row>
          {/*
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>Environment</Col>
              <Col>
                <Select
                  value={device.environment}
                  onChange={v => this.handleDeviceChange('environment', v)}
                  style={{ width: 200 }}
                >
                  {EnvironmentsOptions.map(item => (
                    <Select.Option value={item.value} key={item.value}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </SettingCard.Row>
          */}
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>Power Savings Mode</Col>
              <Col>
                <Switch checked={setting.powerSave} onChange={v => this.handleSettingChange('powerSave', v)} />
              </Col>
            </Row>
          </SettingCard.Row>
          <SettingCard.Row className='padding-v-10'>
            <Row justify='space-between' align='middle'>
              <Col span={16}>
                <ComposedTitle
                  primary='Camera Recording Hours'
                  secondary='Set the time when camera recording features are enabled. Recording will not be allowed outside of this hours to protect user’s privacy.'
                  secondaryStyle={{ whiteSpace: 'normal' }}
                />
              </Col>
              <Col>
                {
                  //@ts-ignore
                  <TimePicker.RangePicker
                    value={[setting.workTimeStart, setting.workTimeEnd]}
                    onChange={this.handleTimeChange}
                    format='HH:mm'
                    style={{ width: 195 }}
                  />
                }
              </Col>
            </Row>
          </SettingCard.Row>
          <SettingCard.CollapseRow
            title={
              <ComposedTitle
                primary='Continuous Capture Settings'
                secondary='Set how cameras should capture images either in time-based or distance based intervals.'
              />
            }
            defaultCollapsed={true}
            headerClassName='no-border-b'
          >
            <div>Choose One:</div>
            <Radio.Group
              style={{ padding: '16px 0px 0px 24px', width: '100%' }}
              value={setting.intervalType}
              onChange={e => this.handleSettingChange('intervalType', e.target.value)}
              // disabled={disabled}
            >
              <Row align='middle' justify={'space-between'} style={{ marginBottom: 16 }} gutter={20}>
                <Col>
                  <Radio value={IntervalType.Time}>Time-Based Interval:</Radio>
                </Col>
                <Col>
                  <Input
                    style={{ width: 195 }}
                    type='number'
                    disabled={setting.intervalType === IntervalType.Distance}
                    prefix={<span style={{ marginRight: 10 }}>Every</span>}
                    suffix={<span style={{ marginLeft: 10 }}>s</span>}
                    min={TimeBasedIntervalRange[0]}
                    max={TimeBasedIntervalRange[1]}
                    onKeyDown={formatNumberInput}
                    value={setting.captureInterval}
                    placeholder='Input a number'
                    onChange={e => this.handleSettingChange('captureInterval', e.target.value)}
                  />
                </Col>
              </Row>
              <Row align='middle' justify={'space-between'} gutter={20}>
                <Col>
                  <Radio value={IntervalType.Distance}>Distance-Based Interval:</Radio>
                </Col>
                <Col>
                  <Input
                    style={{ width: 195 }}
                    type='number'
                    disabled={setting.intervalType === IntervalType.Time}
                    prefix={<span style={{ marginRight: 10 }}>Every</span>}
                    suffix={<span style={{ marginLeft: 10 }}>m</span>}
                    min={DistanceBasedIntervalrange[0]}
                    max={DistanceBasedIntervalrange[1]}
                    onKeyDown={formatNumberInput}
                    value={setting.captureDistanceInterval}
                    placeholder='Input a number'
                    onChange={e => this.handleSettingChange('captureDistanceInterval', e.target.value)}
                  />
                </Col>
              </Row>
            </Radio.Group>
          </SettingCard.CollapseRow>
        </SettingCard>
        <SettingCard
          title='Imagery Upload'
          icon={<CloudUploadOutlined />}
          className='detail-item'
          id='modal-imageryUpload'
        >
          <SettingCard.CollapseRow title='Upload Format and Quality' defaultCollapsed={true}>
            <div>Choose One:</div>
            <Radio.Group
              style={{ padding: '16px 0px 16px 0px', width: '100%' }}
              value={setting.uploadFmt}
              onChange={e => this.handleSettingChange('uploadFmt', e.target.value)}
            >
              <Row align='middle' style={{ marginBottom: 16 }} gutter={20}>
                <Col span={3}>
                  <Radio value={UploadFormat.Video}>Video:</Radio>
                </Col>
                <Col>
                  <Select
                    style={{ width: 166 }}
                    value={setting.videoResolution}
                    onChange={v => this.handleSettingChange('videoResolution', v)}
                    disabled={setting.uploadFmt === UploadFormat.Images}
                  >
                    <Select.Option value={VideoResolution._720}>720p</Select.Option>
                    <Select.Option value={VideoResolution._1080}>1080p</Select.Option>
                    <Select.Option value={VideoResolution._2700}>2.7k</Select.Option>
                    <Select.Option value={VideoResolution._4000}>4k</Select.Option>
                  </Select>
                </Col>
              </Row>
              <Row align='middle' gutter={20}>
                <Col span={3}>
                  <Radio value={UploadFormat.Images}>Images:</Radio>
                </Col>
                <Col>
                  <Select
                    style={{ width: 166 }}
                    value={setting.imageryResolution}
                    onChange={v => this.handleSettingChange('imageryResolution', v)}
                    disabled={setting.uploadFmt === UploadFormat.Video}
                  >
                    <Select.Option value={ImageryResolution._2_MP}>2MP</Select.Option>
                    <Select.Option value={ImageryResolution._5_MP}>5MP</Select.Option>
                    <Select.Option value={ImageryResolution._8_MP}>8MP</Select.Option>
                    <Select.Option value={ImageryResolution._12_MP}>12MP</Select.Option>
                  </Select>
                </Col>

                {setting.uploadFmt === UploadFormat.Images && (
                  <Col style={{ marginLeft: 'auto' }}>
                    <span className='ant-radio-wrapper' style={{ marginRight: 16 }}>
                      Image Quality:
                    </span>
                    <Select
                      style={{ width: 130 }}
                      value={setting.imageCompressRatio}
                      onChange={v => this.handleSettingChange('imageCompressRatio', v)}
                    >
                      {this.imageCompressionOptions}
                    </Select>
                  </Col>
                )}
              </Row>
            </Radio.Group>
          </SettingCard.CollapseRow>
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>
                <ComposedTitle
                  primary='Data Saver Mode'
                  secondary='Encode multiple JPEGs into a single MP4 file to optimize files size. Requires more processing power.'
                />
              </Col>
              <Col>
                <Switch
                  checked={setting.imageryFmt === ImageryFormat.H256}
                  onChange={v => this.handleSettingChange('imageryFmt', v)}
                />
              </Col>
            </Row>
          </SettingCard.Row>
          <SettingCard.Row className='padding-v-10'>
            <Row justify='space-between' align='middle'>
              <Col>Upload Method</Col>
              <Col>
                <Select
                  style={{ width: 195 }}
                  value={setting.uploadMethod}
                  onChange={v => this.handleSettingChange('uploadMethod', v)}
                >
                  <Select.Option value={UploadMethod._4G}>4G</Select.Option>
                  <Select.Option value={UploadMethod.Wifi}>Wifi</Select.Option>
                  <Select.Option value={UploadMethod.Wifi_4G}>4G + Wifi</Select.Option>
                </Select>
              </Col>
            </Row>
          </SettingCard.Row>
          <SettingCard.Row className='padding-v-10'>
            <Row justify='space-between' align='middle'>
              <Col>GPS Upload Interval</Col>
              <Col>
                <Input
                  style={{ width: 195 }}
                  type='number'
                  prefix={<span style={{ marginRight: 10 }}>Every</span>}
                  suffix={<span style={{ marginLeft: 10 }}>s</span>}
                  min={GPSUploadIntervalRange[0]}
                  max={GPSUploadIntervalRange[1]}
                  onKeyDown={formatNumberInput}
                  value={setting.gpsUploadInterval}
                  placeholder='Input a number'
                  onChange={e => this.handleSettingChange('gpsUploadInterval', e.target.value)}
                />
              </Col>
            </Row>
          </SettingCard.Row>
          <SettingCard.Row className='padding-v-10'>
            <Row justify='space-between' align='middle'>
              <Col>IMU Upload Interval</Col>
              <Col>
                <Input
                  style={{ width: 195 }}
                  type='number'
                  prefix={<span style={{ marginRight: 10 }}>Every</span>}
                  suffix={<span style={{ marginLeft: 10 }}>s</span>}
                  min={IMUUploadIntervalRange[0]}
                  max={IMUUploadIntervalRange[1]}
                  onKeyDown={formatNumberInput}
                  value={setting.imuUploadInterval}
                  placeholder='Input a number'
                  onChange={e => this.handleSettingChange('imuUploadInterval', e.target.value)}
                />
              </Col>
            </Row>
          </SettingCard.Row>
        </SettingCard>
        <SettingCard id='modal-user' title='User' icon={<UserOutlined />} className='detail-item'>
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>DAX</Col>
              <Col>
                <Input
                  value={device.driverID}
                  onChange={(e) => this.handleDeviceChange('driverID', e.target.value)}
                />
              </Col>
            </Row>
          </SettingCard.Row>
        </SettingCard>
        <SettingCard title='AI Models' icon={<ForkOutlined />} className='detail-item' id='modal-aiModels'>
          {SupportedAIModels.map(({ name, key }) => (
            <SettingCard.Row key={key}>
              <Row justify='space-between' align='middle'>
                <Col>
                  <div>{name}</div>
                  {/*<Typography.Text type='secondary'>Latest Version: 1.235 </Typography.Text><Button type='link'>update</Button>*/}
                </Col>
                <Col>
                  <Switch checked={setting[key]} onChange={checked => this.handleSettingChange(key, checked)} />
                </Col>
              </Row>
            </SettingCard.Row>
          ))}
        </SettingCard>
        <SettingCard title='Data Quota' icon={<BarChartOutlined />} className='detail-item' id='modal-dataUsage'>
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>
                <ComposedTitle
                  primary='Monthly Data Quota:'
                  secondary={`Quota usage limitation (max ${MonthlyDataQuotaRange[1]})`}
                />
              </Col>
              <Col>
                <Input
                  style={{ width: 165 }}
                  type='number'
                  min={MonthlyDataQuotaRange[0]}
                  max={MonthlyDataQuotaRange[1]}
                  onKeyDown={formatNumberInput}
                  value={setting.trafficQuota}
                  placeholder='Input a number'
                  onChange={v => this.handleSettingChange('trafficQuota', v.target.value)}
                />
                <span style={{ marginLeft: 10 }}>GB</span>
              </Col>
            </Row>
          </SettingCard.Row>

          <SettingCard.CollapseRow title='Data Management Cycle' defaultCollapsed={true} headerClassName='no-border-b'>
            <div>Choose One:</div>
            <Radio.Group
              style={{ padding: '16px 24px 0px 24px' }}
              value={quotaCycleType}
              onChange={e => this.handleQuotaCycleType(e.target.value)}
            >
              <Row align='middle' style={{ marginBottom: 16 }} gutter={20}>
                <Col>
                  <Radio value={QuotaCycle.CalendarMonth}>Per Calendar Month</Radio>
                </Col>
              </Row>
              <Row align='middle' gutter={20}>
                <Col>
                  <Radio value={QuotaCycle.PickedDay}>Per billing cycle starting on every:</Radio>
                </Col>
                <Col>
                  <Select
                    style={{ width: 166 }}
                    disabled={quotaCycleType === QuotaCycle.CalendarMonth}
                    value={
                      quotaCycleType === QuotaCycle.CalendarMonth ? 'Monthly starting date' : device.billingCycleStart
                    }
                    onChange={v => this.handleDeviceChange('billingCycleStart', v)}
                  >
                    {CycleStartDateOptions}
                  </Select>
                </Col>
                <Col>
                  <span style={{ fontSize: 12 }}>of the month</span>
                </Col>
              </Row>
            </Radio.Group>
          </SettingCard.CollapseRow>

          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>
                <ComposedTitle primary='Data alert Range:' secondary='Warn me when a camera is within this range.' />
              </Col>
              <Col>
                <Select
                  style={{ width: 74 }}
                  value={setting.alertThreshold}
                  onChange={v => this.handleSettingChange('alertThreshold', v)}
                >
                  {AlertThresholdOptions}
                </Select>
                <span style={{ marginLeft: 10 }}>of monthly quota</span>
              </Col>
            </Row>
          </SettingCard.Row>
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>
                <ComposedTitle
                  primary='Data Hard Limit:'
                  secondary='Stop all upload from a camera within this range.'
                />
              </Col>
              <Col>
                <Select
                  style={{ width: 74 }}
                  value={setting.hardThreshold}
                  onChange={v => this.handleSettingChange('hardThreshold', v)}
                >
                  {HardThresholdOptions}
                </Select>
                <span style={{ marginLeft: 10 }}>of monthly quota</span>
              </Col>
            </Row>
          </SettingCard.Row>
        </SettingCard>
      </>
    )
  }

  renderIfTestModeDisabled = () => {
    const { device } = this.state.detail
    const { quotaCycleType } = this.state
    const { cities, device: { deviceID, cityID } } = this.props
    let mode = device.deviceType === DeviceType.Lite360Master ?  settingMode.operate : settingMode.query
    let originGroupCamera = { front: { deviceID, direction: CameraDirection.front, cityID } }
    if (this.originGroupCamera !== null) {
      Object.keys(this.originGroupCamera).forEach(key => {
        originGroupCamera[key] = { ...this.originGroupCamera[key], cityID }
      })
    }

    return (
      <>
        <SettingCard id='modal-camera' title='Camera' icon={<CameraOutlined />} className='detail-item'>
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>Test Mode</Col>
              <Col>
                <Switch checked={device.testMode} onChange={v => this.handleDeviceChange('testMode', v)} />
              </Col>
            </Row>
          </SettingCard.Row>
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>Camera Type</Col>
              <Col>
                <Select
                  value={device.deviceType === DeviceType.Lite360Slave ? DeviceType.Lite360Master : device.deviceType}
                  onChange={v => this.handleDeviceChange('deviceType', v)}
                  style={{ width: 200 }}
                >
                  <Select.Option value={DeviceType.TwoW}>2W</Select.Option>
                  <Select.Option value={DeviceType.FourW}>4W</Select.Option>
                  <Select.Option
                    value={DeviceType.Lite360Master}
                    disabled={Number(deviceID.slice(-1)) < 3}
                  >
                    360-Lite
                  </Select.Option>
                </Select>
              </Col>
            </Row>
          </SettingCard.Row>
          {
            (device.deviceType === DeviceType.Lite360Master || device.deviceType === DeviceType.Lite360Slave)
            && (
              <SettingCard.Row
                className={((device.deviceType === DeviceType.Lite360Master) || (device.deviceType === DeviceType.Lite360Slave)) ? '' : 'hide'}
              >
                <CameraGroupSetting
                  values={{ ...originGroupCamera }}
                  onChange={this.handleCameraGroupSettingChange}
                  mode={mode}
                />
              </SettingCard.Row>
            )
          }
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>Task Type</Col>
              <Col>
                <Select
                  value={device.taskType}
                  onChange={v => this.handleDeviceChange('taskType', v)}
                  style={{ width: 200 }}
                >
                  <Select.Option value={TaskType.Passive}>Passive</Select.Option>
                  <Select.Option value={TaskType.Active}>Active</Select.Option>
                </Select>
              </Col>
            </Row>
          </SettingCard.Row>
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>Current Location</Col>
              <Col>
                <Select
                  value={device.cityID}
                  onChange={v => this.handleDeviceChange('cityID', v)}
                  style={{ width: 200 }}
                >
                  {cities.map(d => (
                    <Select.Option value={d.id} key={d.id}>
                      {d.name}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </SettingCard.Row>
          {/*
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>Environment</Col>
              <Col>
                <Select
                  value={device.environment}
                  onChange={v => this.handleDeviceChange('environment', v)}
                  style={{ width: 200 }}
                >
                  {EnvironmentsOptions.map(item => (
                    <Select.Option value={item.value} key={item.value}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </SettingCard.Row>
          */}
        </SettingCard>
        <SettingCard id='modal-user' title='User' icon={<UserOutlined />} className='detail-item'>
          <SettingCard.Row>
            <Row justify='space-between' align='middle'>
              <Col>DAX</Col>
              <Col>
                <Input
                  value={device.driverID}
                  onChange={e => this.handleDeviceChange('driverID', e.target.value)}
                />
              </Col>
            </Row>
          </SettingCard.Row>
        </SettingCard>

        <SettingCard title='Data Quota' icon={<BarChartOutlined />} className='detail-item' id='modal-dataUsage'>
          <SettingCard.CollapseRow title='Data Management Cycle' defaultCollapsed={true} headerClassName='no-border-b'>
            <div>Choose One:</div>
            <Radio.Group
              style={{ padding: '16px 24px 0px 24px' }}
              value={quotaCycleType}
              onChange={e => this.handleQuotaCycleType(e.target.value)}
            >
              <Row align='middle' style={{ marginBottom: 16 }} gutter={20}>
                <Col>
                  <Radio value={QuotaCycle.CalendarMonth}>Per Calendar Month</Radio>
                </Col>
              </Row>
              <Row align='middle' gutter={20}>
                <Col>
                  <Radio value={QuotaCycle.PickedDay}>Per billing cycle starting on every:</Radio>
                </Col>
                <Col>
                  <Select
                    style={{ width: 166 }}
                    disabled={quotaCycleType === QuotaCycle.CalendarMonth}
                    value={
                      quotaCycleType === QuotaCycle.CalendarMonth ? 'Monthly starting date' : device.billingCycleStart
                    }
                    onChange={v => this.handleDeviceChange('billingCycleStart', v)}
                  >
                    {CycleStartDateOptions}
                  </Select>
                </Col>
                <Col>
                  <span style={{ fontSize: 12 }}>of the month</span>
                </Col>
              </Row>
            </Radio.Group>
          </SettingCard.CollapseRow>
        </SettingCard>
      </>
    )
  }

  private handleSubmit = async () => {
    let { device: { deviceID, groupID }, onOk } = this.props
    const { device, setting } = this.state.detail
    const { groupSettingDirty, groupCameraSetting } = this.state
    let resp
    let groupSetting: ICameraGroupQuerySetting = {
      slaves: null
    }

    if (!device) {
      return
    }

    this.setState({
      saving: true
    })

    try {
      if (setting && this.editingModel.isSettingModelDirty()) {
        const settingUpdateParam = this.editingModel.getSettingModelDirtyFields()
        resp = await updateDeviceSetting(deviceID, settingUpdateParam)
      }
      if (this.editingModel.isDeviceModelDirty()) {
        const masterDeviceDirection = cameraDirectionDictionary.front
        let deviceUpdateParam: IDeviceUdpateParam = this.editingModel.getDeviceModelDirtyFields()
        const { deviceType } = deviceUpdateParam
        if (deviceType === DeviceType.Lite360Master) {
          deviceUpdateParam = { ...deviceUpdateParam, masterDeviceDirection }
        }
        resp = await updateDevice(deviceID, deviceUpdateParam)
      }
      if (groupSettingDirty) {
        if (groupID === 0 || groupID === null) {
          groupID = resp.groupID
        }
        groupSetting.slaves = [
          groupCameraSetting.left || { deviceID: '', direction: CameraDirection.left },
          groupCameraSetting.back || { deviceID: '', direction: CameraDirection.back },
          groupCameraSetting.right || { deviceID: '', direction: CameraDirection.right }
        ]
          .map(({ deviceID, direction }) => ({ deviceID, direction }))
        await updateCameraGroupSetting(groupID, groupSetting)
      }

      message.success('update successfully')
      onOk(null)

      if (resp?.onlineStatus === 0 && this.editingModel.getDeviceModelDirtyFields().deviceType != null) {
        Modal.info({
          title: 'Camera type switch',
          content: 'The camera is offline. Its type will be synced once it is online.'
        })
      }
    } catch (error) {
      console.log(error)
      Modal.error({ title: error?.response?.status, content: error?.response?.data })
      this.setState({
        saving: false
      })
    }
  }

  private handleReset = () => {
    this.editingModel.reset()
  }

  private renderContent = () => {
    const {
      testMode,
      detail: { device }
    } = this.state
    let content = null
    if (device) {
      const cards = testMode && device.testMode ? this.renderIfTestModeEnabled() : this.renderIfTestModeDisabled()

      content = (
        <>
          {this.renderAnchor()}
          <div className='content' ref={this.anchorContainer}>
            {cards}
            <div style={{ height: 500 }} />
          </div>
        </>
      )
    }
    return content
  }

  render() {
    const { visible, onCancel, device: { deviceID } } = this.props
    const { loading, saving, deviceDirty, settingDirty, groupSettingDirty } = this.state
    const title = (
      <Row justify='space-between' align='middle'>
        <Col>
          <Typography.Title level={3}>Change Settings {deviceID}</Typography.Title>
        </Col>
        <Col>
          <Button onClick={this.handleReset}>Reset Changes</Button>
        </Col>
      </Row>
    )
    return (
      <Modal
        title={title}
        closable={false}
        visible={visible}
        className='detail-editing-modal'
        cancelText='Dismiss'
        okText='Done'
        onOk={this.handleSubmit}
        onCancel={onCancel}
        confirmLoading={saving}
        okButtonProps={{ disabled: !deviceDirty && !settingDirty && !groupSettingDirty }}
        width={750}
      >
        <div className='detail-editing'>
          {this.renderContent()}
          {loading && <MaskLoading />}
        </div>
      </Modal>
    )
  }
}

export default connect(mapState)(DetailEditingModal)
